import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Upload } from "lucide-react";
import api from "@/lib/api";

// Generic no-code CRUD manager for a collection.
// fields: [{ key, label, type: text|textarea|number|bool|select|image, options?, placeholder?, full? }]
export default function CrudManager({ endpoint, listKey, title, subtitle, fields, testid }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(() => blank(fields));
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/${endpoint}`);
      setItems(data[listKey] || []);
    } catch { toast.error(`Failed to load ${title}`); }
  }, [endpoint, listKey, title]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(blank(fields)); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    const required = fields.filter((f) => f.required);
    for (const r of required) {
      if (!String(form[r.key] ?? "").trim()) { toast.error(`${r.label} is required`); return; }
    }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/${endpoint}/${editingId}`, form);
        toast.success(`${title} updated`);
      } else {
        await api.post(`/admin/${endpoint}`, form);
        toast.success(`${title} added`);
      }
      reset();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.toString?.() || "Save failed");
    } finally { setBusy(false); }
  };

  const edit = (item) => {
    const next = blank(fields);
    fields.forEach((f) => { next[f.key] = item[f.key] ?? next[f.key]; });
    setForm(next);
    setEditingId(item.id);
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${title.toLowerCase()}?`)) return;
    await api.delete(`/admin/${endpoint}/${id}`);
    setItems((p) => p.filter((x) => x.id !== id));
    toast.success(`${title} deleted`);
  };

  const primaryField = fields.find((f) => ["title", "name"].includes(f.key)) || fields[0];
  const imageField = fields.find((f) => f.type === "image");

  return (
    <div data-testid={`crud-${testid}`}>
      <div className="grid gap-8 lg:grid-cols-12">
        {/* Form */}
        <form onSubmit={submit} className="rounded-2xl border border-[#e7e0d6] bg-white p-6 card-shadow lg:col-span-5" data-testid={`${testid}-form`}>
          <div className="eyebrow text-[#d46a1d]">{editingId ? "Edit" : "Add"} {title}</div>
          <h3 className="mt-1 font-serif text-2xl text-[#202124]">{subtitle || title}</h3>
          <div className="mt-5 space-y-4">
            {fields.map((f) => (
              <div key={f.key}>
                <label className="mb-1 block text-sm font-medium text-[#4a4a48]">{f.label}{f.required && <span className="text-[#d46a1d]"> *</span>}</label>
                {f.type === "textarea" ? (
                  <textarea rows={f.rows || 3} className="form-field resize-none" value={form[f.key] ?? ""} placeholder={f.placeholder}
                    onChange={(e) => set(f.key, e.target.value)} data-testid={`${testid}-${f.key}`} />
                ) : f.type === "bool" ? (
                  <label className="flex items-center gap-2 text-sm text-[#4a4a48]">
                    <input type="checkbox" checked={!!form[f.key]} onChange={(e) => set(f.key, e.target.checked)} data-testid={`${testid}-${f.key}`} />
                    {f.hint || "Enabled"}
                  </label>
                ) : f.type === "select" ? (
                  <select className="form-field" value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`${testid}-${f.key}`}>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "number" ? (
                  <input type="number" className="form-field" value={form[f.key] ?? 0} onChange={(e) => set(f.key, Number(e.target.value))} data-testid={`${testid}-${f.key}`} />
                ) : f.type === "image" ? (
                  <ImageField
                    value={form[f.key] ?? ""}
                    onChange={(v) => set(f.key, v)}
                    testid={`${testid}-${f.key}`}
                    placeholder={f.placeholder || "https://... or upload"}
                  />
                ) : (
                  <input className="form-field" value={form[f.key] ?? ""} placeholder={f.placeholder}
                    onChange={(e) => set(f.key, e.target.value)} data-testid={`${testid}-${f.key}`} />
                )}
                {f.type === "image" && form[f.key] && (
                  <img src={form[f.key]} alt="" className="mt-2 h-20 w-full rounded-lg object-cover" />
                )}
              </div>
            ))}
            <div className="flex gap-2">
              <button type="submit" disabled={busy} data-testid={`${testid}-submit`}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#d46a1d] px-5 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
                {editingId ? <><Check size={15} /> Update</> : <><Plus size={15} /> Add {title}</>}
              </button>
              {editingId && (
                <button type="button" onClick={reset} className="inline-flex items-center gap-2 rounded-full border border-[#d8cfc1] px-5 py-3 text-sm font-medium">
                  <X size={15} /> Cancel
                </button>
              )}
            </div>
          </div>
        </form>

        {/* List */}
        <div className="lg:col-span-7">
          <h3 className="mb-4 font-serif text-2xl text-[#202124]">All {title} ({items.length})</h3>
          <div className="space-y-3">
            {items.length === 0 && <div className="rounded-2xl border border-[#e7e0d6] bg-white p-8 text-center text-[#6b6a65]">Nothing yet — add your first {title.toLowerCase()}.</div>}
            {items.map((it) => (
              <div key={it.id} className="flex items-center gap-4 rounded-2xl border border-[#e7e0d6] bg-white p-4 card-shadow" data-testid={`${testid}-row-${it.id}`}>
                {imageField && it[imageField.key] && (
                  <img src={it[imageField.key]} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-[#202124]">{it[primaryField.key] || "(untitled)"}</span>
                    {"trending" in it && it.trending && <span className="rounded-full bg-[#fff1e3] px-2 py-0.5 text-[10px] font-semibold uppercase text-[#d46a1d]">Trending</span>}
                    {"active" in it && !it.active && <span className="rounded-full border border-[#d8cfc1] px-2 py-0.5 text-[10px] uppercase text-[#8a8880]">Hidden</span>}
                    {"published" in it && !it.published && <span className="rounded-full border border-[#d8cfc1] px-2 py-0.5 text-[10px] uppercase text-[#8a8880]">Draft</span>}
                  </div>
                  <div className="truncate text-sm text-[#6b6a65]">{it.category || it.role || it.subtitle || it.excerpt || it.tagline || it.quote || ""}</div>
                </div>
                <div className="flex shrink-0 gap-3">
                  <button onClick={() => edit(it)} data-testid={`${testid}-edit-${it.id}`} className="text-[#a8a498] hover:text-[#d46a1d]"><Pencil size={16} /></button>
                  <button onClick={() => remove(it.id)} data-testid={`${testid}-delete-${it.id}`} className="text-[#a8a498] hover:text-[#d46a1d]"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function blank(fields) {
  const o = {};
  fields.forEach((f) => {
    o[f.key] = f.type === "bool" ? (f.default ?? false) : f.type === "number" ? (f.default ?? 0) : (f.default ?? "");
  });
  return o;
}

function ImageField({ value, onChange, testid, placeholder }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)"); return; }
    setBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/admin/media", {
        filename: file.name,
        content_type: file.type || "image/png",
        data_base64: dataUrl,
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2">
      <input
        className="form-field flex-1"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
      <label className={`inline-flex cursor-pointer items-center gap-1 rounded-lg border border-[#d8cfc1] px-3 text-xs font-medium text-[#4a4a48] hover:border-[#d46a1d] hover:text-[#d46a1d] ${busy ? "opacity-60" : ""}`} data-testid={`${testid}-upload`}>
        <Upload size={13} /> {busy ? "…" : "Upload"}
        <input
          type="file"
          className="hidden"
          accept="image/*"
          disabled={busy}
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </label>
    </div>
  );
}
